import React, { useEffect, useState, useCallback } from 'react';
import { FiMonitor } from 'react-icons/fi';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';

/**
 * My Devices card (Phase 3 Sprint E / E3)
 *
 * Desktop installs registered to the account (dta_devices), up to MAX_DEVICES,
 * with unregister. Lives on the Account Management page (devices are an account
 * concern). Reads /bff/devices; registration flows up from the desktop on launch.
 */

const PLATFORM_LABEL = { win32: 'Windows', darwin: 'macOS', linux: 'Linux' };
const MAX_DEVICES = 2;

function timeAgo(ts) {
  if (!ts) return 'never';
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function DevicesCard() {
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/bff/devices`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => setDevices(Array.isArray(data.devices) ? data.devices : []))
      .catch((err) => setError(err.message || 'Failed to load devices'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeDevice = useCallback(async (id, name) => {
    if (!window.confirm(`Remove "${name || 'this device'}"? You can re-register it by reopening the desktop app.`)) return;
    setRemovingId(id);
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/devices/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDevices((prev) => (prev || []).filter((d) => d.id !== id));
    } catch (err) {
      window.alert('Could not remove device: ' + (err.message || 'unknown error'));
    } finally {
      setRemovingId(null);
    }
  }, []);

  const list = devices || [];

  return (
    <div className="border-2 border-gray-200 rounded-2xl p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
            <FiMonitor className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-black">My Devices</h2>
            <p className="text-gray-500 text-sm">Desktop installs registered to your account</p>
          </div>
        </div>
        <span className="text-sm text-gray-500">
          {loading ? '—' : `${list.length} of ${MAX_DEVICES}`}
        </span>
      </div>

      {error && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-3 text-gray-500 text-sm py-6">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
          Loading devices&hellip;
        </div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center">
          <p className="text-gray-700 font-medium">No devices registered yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Open the NoSCAM desktop app and sign in &mdash; it registers here automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((d) => (
            <div key={d.id} className="border border-gray-200 rounded-xl p-4 bg-white flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold text-black truncate">{d.device_name || 'Unnamed device'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {(PLATFORM_LABEL[d.platform] || d.platform || 'Unknown')}
                  {d.app_version ? ` · v${d.app_version}` : ''}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Last sync {timeAgo(d.last_sync_at)} · added {d.first_seen_at ? new Date(d.first_seen_at).toLocaleDateString() : '—'}
                </p>
              </div>
              <button
                onClick={() => removeDevice(d.id, d.device_name)}
                disabled={removingId === d.id}
                className="shrink-0 text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {removingId === d.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
